import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface MobileBottomNavProps {
    onToggleMenu?: () => void;
    isPlatformAdmin?: boolean;
}

interface NavItem {
    id: string;
    label: string;
    path?: string;
    action?: () => void;
    svg: React.ReactNode;
    isActive: boolean;
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ onToggleMenu, isPlatformAdmin = false }) => {
    const location = useLocation();
    const navigate = useNavigate();

    const currentPath = location.pathname;

    const baseNavItems: NavItem[] = [
        {
            id: 'panel',
            label: 'Panel',
            path: '/',
            svg: (
                <svg style={{ width: '22px', height: '22px', minWidth: '22px', minHeight: '22px', flexShrink: 0, display: 'block', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }} viewBox="0 0 24 24">
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
            ),
            isActive: currentPath === '/'
        },
        {
            id: 'plan',
            label: 'Plan',
            path: '/planning',
            svg: (
                <svg style={{ width: '22px', height: '22px', minWidth: '22px', minHeight: '22px', flexShrink: 0, display: 'block', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }} viewBox="0 0 24 24">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
            ),
            isActive: currentPath.startsWith('/planning')
        },
        {
            id: 'calidad',
            label: 'Calidad',
            path: '/quality',
            svg: (
                <svg style={{ width: '22px', height: '22px', minWidth: '22px', minHeight: '22px', flexShrink: 0, display: 'block', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }} viewBox="0 0 24 24">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M9 12l2 2 4-4" />
                </svg>
            ),
            isActive: currentPath.startsWith('/quality')
        },
        {
            id: 'sc',
            label: 'SC',
            path: '/payments',
            svg: (
                <svg style={{ width: '22px', height: '22px', minWidth: '22px', minHeight: '22px', flexShrink: 0, display: 'block', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }} viewBox="0 0 24 24">
                    <rect x="2" y="7" width="20" height="14" rx="2" />
                    <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
                </svg>
            ),
            isActive: currentPath.startsWith('/payments')
        }
    ];

    // Todos los usuarios (incluidos administradores) ven exactamente las 4 pestañas operativas móviles
    const navItems: NavItem[] = baseNavItems;

    return (
        <nav
            className="kl-bottom-nav"
            style={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                height: 'calc(56px + var(--kl-safe-bottom, env(safe-area-inset-bottom, 0px)))',
                background: '#ffffff',
                borderTop: '1px solid var(--kl-border, #E2E8F0)',
                display: 'flex',
                flexDirection: 'column',
                zIndex: 50,
                boxSizing: 'border-box'
            }}
        >
            {/* Fila principal superior de 56px para botones y etiquetas */}
            <div
                style={{
                    height: '56px',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'stretch',
                    flexShrink: 0
                }}
            >
                {navItems.map((item) => {
                    const handleClick = () => {
                        if (item.id === 'plan') {
                            sessionStorage.setItem('planning_activeTab', 'semanal');
                        }
                        if (item.action) {
                            item.action();
                        } else if (item.path) {
                            navigate(item.path);
                        }
                    };

                    return (
                        <button
                            key={item.id}
                            type="button"
                            onClick={handleClick}
                            style={{
                                flex: 1,
                                height: '56px',
                                border: 0,
                                background: 'none',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '3px',
                                fontSize: '11px',
                                color: item.isActive ? 'var(--kl-primary, #2563EB)' : 'var(--kl-text-4, #64748B)',
                                fontWeight: item.isActive ? 700 : 500,
                                position: 'relative',
                                cursor: 'pointer',
                                padding: 0,
                                userSelect: 'none'
                            }}
                        >
                            {/* Indicador de 3px sobre el ítem activo */}
                            {item.isActive && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: '25%',
                                        right: '25%',
                                        height: '3px',
                                        background: 'var(--kl-primary, #2563EB)',
                                        borderRadius: '0 0 3px 3px'
                                    }}
                                />
                            )}
                            {item.svg}
                            <span style={{ lineHeight: 1, letterSpacing: '-0.01em' }}>{item.label}</span>
                        </button>
                    );
                })}
            </div>

            {/* Franja reservada de zona segura inferior */}
            <div
                style={{
                    height: 'var(--kl-safe-bottom, env(safe-area-inset-bottom, 0px))',
                    width: '100%',
                    flexShrink: 0
                }}
            />
        </nav>
    );
};
